import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  ReceiptText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import type { ThesisPrintRequest } from "@/lib/api/bindery";
import { invoicesApi } from "@/lib/api/invoices";
import {
  formatDate,
  formatMoney,
  THESIS_PAYMENT_METHOD_LABELS,
} from "@/pages/bindery/binderyFormatters";

interface Props {
  req: ThesisPrintRequest;
}

export function PatronPaymentPanel({ req }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const [redirecting, setRedirecting] = useState(false);

  if (req.paidAt) {
    const online = req.paymentMethod === "PAYSTACK" || req.paymentMethod === "FLUTTERWAVE";
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <div className="flex items-center gap-2 font-semibold">
          <CheckCircle2 className="h-4 w-4" /> Payment received
        </div>
        <p className="mt-1">
          {formatMoney(req.cost)} paid on {formatDate(req.paidAt)}
          {req.paymentMethod
            ? ` via ${THESIS_PAYMENT_METHOD_LABELS[req.paymentMethod] ?? req.paymentMethod}`
            : ""}
          {req.paymentReference ? ` · Ref ${req.paymentReference}` : ""}.
        </p>
        {req.invoiceId && (
          <Button
            variant="link"
            size="sm"
            className="mt-1 h-auto px-0 text-emerald-800"
            onClick={() => navigate(`/invoices`)}
          >
            <ReceiptText className="mr-1 h-3.5 w-3.5" />
            View invoice {online ? "/ receipt" : ""}
          </Button>
        )}
      </div>
    );
  }

  if (req.status !== "APPROVED") return null;

  const payOnline = async () => {
    if (!req.invoiceId) {
      toast.error("No invoice attached to this request. Please contact the library.");
      return;
    }
    setRedirecting(true);
    try {
      const res = await invoicesApi.initiatePayment(req.invoiceId);
      if (res.authorizationUrl) {
        window.location.href = res.authorizationUrl;
      } else {
        toast.error("Could not start payment. Please try again.");
        setRedirecting(false);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to start payment"));
      setRedirecting(false);
    }
  };

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      <div className="flex items-center gap-2 font-semibold">
        <CreditCard className="h-4 w-4" /> Payment required to start printing
      </div>
      <p className="mt-1">
        Your submission was approved. Please pay{" "}
        <span className="font-semibold">{formatMoney(req.cost)}</span> to begin printing and
        binding.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={payOnline} disabled={redirecting || !req.invoiceId} className="gap-2">
          {redirecting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ExternalLink className="h-4 w-4" />
          )}
          Pay online with Paystack
        </Button>
        {req.invoiceId && (
          <Button variant="outline" onClick={() => navigate("/invoices")}>
            <ReceiptText className="mr-2 h-4 w-4" /> View invoice
          </Button>
        )}
      </div>
      <div className="mt-3 flex items-start gap-2 text-xs text-amber-800/80">
        <Building2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Prefer to pay in person? Visit the library desk — staff will record your cash, bank
          transfer or POS payment.
        </span>
      </div>
    </div>
  );
}
